// ============================================================================
//  ActivityCorrections.tsx — Rectifications tracées des activités CRM (B56)
//  Une activité n'est jamais modifiée ni supprimée : son auteur peut seulement
//  ajouter une rectification datée et signée, affichée sous l'activité d'origine.
// ============================================================================

import { useEffect, useId, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { PencilLine, X } from 'lucide-react'
import { crmApi, type CrmActivity, type CrmActivityCorrection } from '@/modules/crm/api/crm.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore } from '@/stores/auth.store'
import { errMessage } from '@/components/ui'

const MAX_LENGTH = 5000

const TYPE_LABELS: Record<string, string> = {
  CALL: 'Appel', EMAIL: 'Email', MEETING: 'Réunion', VIDEO_CALL: 'Visio',
  WHATSAPP: 'WhatsApp', DEMO: 'Démo', NOTE: 'Note', OTHER: 'Autre',
}

type ActivityWithCorrections = Pick<CrmActivity, 'id'> & Partial<CrmActivity>

const correctionsOf = (activity: ActivityWithCorrections): CrmActivityCorrection[] =>
  Array.isArray(activity.corrections) ? activity.corrections : []

const fmtDateTime = (d?: string) =>
  d ? new Date(d).toLocaleString('fr-SN', { dateStyle: 'short', timeStyle: 'short' }) : '—'
const fmtDay = (d?: string) => (d ? new Date(d).toLocaleDateString('fr-SN') : '—')

// ── Badge « Rectifiée » ──────────────────────────────────────────────────────

export function ActivityCorrectedBadge({ activity }: { activity: ActivityWithCorrections }) {
  const n = correctionsOf(activity).length
  if (n === 0) return null
  return (
    <span
      title={`Cette activité a fait l'objet de ${n} rectification${n > 1 ? 's' : ''}`}
      className="inline-flex items-center shrink-0 px-1.5 py-0.5 rounded-md text-[11px] font-semibold leading-none"
      style={{ background: 'var(--amber-bg)', color: 'var(--amber-text)' }}
    >
      Rectifiée
    </span>
  )
}

// ── Liste des rectifications (sous l'activité) ───────────────────────────────

export function ActivityCorrectionList({ activity }: { activity: ActivityWithCorrections }) {
  const corrections = correctionsOf(activity)
  if (corrections.length === 0) return null
  return (
    <ol className="space-y-2" aria-label="Rectifications">
      {corrections.map(c => (
        <li key={c.id} className="pl-3 py-0.5" style={{ borderLeft: '2px solid var(--amber)' }}>
          <p className="text-[11px] font-medium" style={{ color: 'var(--amber-text)' }}>
            Rectification · <time dateTime={c.createdAt}>{fmtDateTime(c.createdAt)}</time> ·{' '}
            {c.author ? `${c.author.firstName} ${c.author.lastName}` : '—'}
          </p>
          <p className="text-[13px] leading-relaxed whitespace-pre-wrap break-words mt-0.5" style={{ color: '#2D2546' }}>
            {c.content}
          </p>
        </li>
      ))}
    </ol>
  )
}

// ── Bouton « Rectifier » (auteur uniquement) ─────────────────────────────────

export function CorrectActivityButton({ activity, invalidateKeys }: {
  activity: ActivityWithCorrections
  invalidateKeys: QueryKey[]
}) {
  const userId    = useAuthStore(s => s.user?.id)
  const canUpdate = useAuthStore(s => s.hasPermission('crm:update'))
  const [open, setOpen] = useState(false)
  // Seul l'auteur de l'activité, avec la permission crm:update, peut la rectifier (contrôle refait côté serveur)
  const authorId = activity.performedBy?.id ?? activity.performedById
  if (!userId || !canUpdate || authorId !== userId) return null
  return (
    <>
      <button
        type="button"
        onClick={e => { e.stopPropagation(); setOpen(true) }}
        aria-haspopup="dialog"
        aria-label={activity.subject ? `Rectifier l'activité « ${activity.subject} »` : "Rectifier l'activité"}
        className="inline-flex items-center gap-1.5 min-h-[44px] sm:min-h-[32px] px-2 rounded-lg text-xs font-medium transition-colors text-[var(--brand-amber)] hover:bg-[#F4F2FC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-orange)]"
      >
        <PencilLine className="w-3.5 h-3.5" aria-hidden="true" /> Rectifier
      </button>
      {open && (
        <CorrectActivityModal activity={activity} invalidateKeys={invalidateKeys} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

// ── Modale d'ajout d'une rectification ───────────────────────────────────────

export function CorrectActivityModal({ activity, invalidateKeys, onClose }: {
  activity: ActivityWithCorrections
  invalidateKeys: QueryKey[]
  onClose: () => void
}) {
  const queryClient     = useQueryClient()
  const addNotification = useNotificationStore(s => s.addNotification)
  const dialogRef   = useRef<HTMLDialogElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const uid = useId()
  const titleId = `${uid}-title`, fieldId = `${uid}-field`, helpId = `${uid}-help`, countId = `${uid}-count`, errorId = `${uid}-error`
  const [content, setContent] = useState('')
  // Garde synchrone contre la double soumission (une rectification est définitive)
  const submittingRef = useRef(false)

  const mutation = useMutation({
    mutationFn: (text: string) => crmApi.correctActivity(activity.id, text),
    onSuccess: async () => {
      await Promise.all(invalidateKeys.map(queryKey => queryClient.invalidateQueries({ queryKey })))
      // Fermer d'abord : le toast ne doit pas rester sous le top layer de la modale
      dialogRef.current?.close()
      addNotification({ type: 'success', title: 'Rectification ajoutée', message: '' })
    },
    onError: () => { textareaRef.current?.focus() },
    onSettled: () => { submittingRef.current = false },
  })
  // aria-invalid seulement pour une erreur de validation (400), pas pour 403/404/réseau
  const isValidationError = mutation.isError
    && (mutation.error as { response?: { status?: number } } | null)?.response?.status === 400

  // Modale native <dialog> ouverte avec showModal() : piège du focus, Échap, inertie
  // du fond et retour du focus sur le bouton déclencheur gérés par le navigateur.
  // Toute fermeture passe par dialog.close() (événement « close » → onClose) ; pas de
  // close() au démontage, qui relancerait onClose sous StrictMode.
  useEffect(() => {
    const dlg = dialogRef.current
    if (dlg && !dlg.open) dlg.showModal()
    textareaRef.current?.focus()
    // Bloque le défilement du fond pendant l'ouverture, comme le Modal du projet
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prevOverflow }
  }, [])

  const trimmedLength = content.trim().length
  const canSubmit = trimmedLength > 0 && !mutation.isPending
  const close = () => { if (!mutation.isPending) dialogRef.current?.close() }

  const sectionLabel = 'text-[11px] font-semibold text-[var(--tx3)] mb-0.5'

  return ReactDOM.createPortal(
    <dialog
      ref={dialogRef}
      aria-labelledby={titleId}
      aria-modal="true"
      onCancel={e => { if (mutation.isPending) e.preventDefault() }}
      onClose={onClose}
      // Les événements React remontent à travers le portail : on évite de déplier/replier la ligne parente
      onClick={e => e.stopPropagation()}
      className="bg-white border border-[var(--bd)] rounded-2xl p-0 w-[calc(100%-2rem)] max-w-lg max-h-[90vh] text-[var(--brand-dark)] shadow-2xl backdrop:bg-[rgba(13,8,32,0.50)] backdrop:backdrop-blur-sm"
    >
      <form
        method="dialog"
        className="flex flex-col max-h-[90vh]"
        onSubmit={e => {
          e.preventDefault()
          if (!canSubmit || submittingRef.current) return
          submittingRef.current = true
          mutation.mutate(content.trim())
        }}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[var(--bd)] shrink-0">
          <h2 id={titleId} className="text-base font-semibold text-[var(--brand-dark)]">Rectifier l'activité</h2>
          <button
            type="button"
            onClick={close}
            disabled={mutation.isPending}
            aria-label="Fermer"
            className="inline-flex items-center justify-center w-11 h-11 sm:w-8 sm:h-8 -mr-2 rounded-lg text-[var(--tx3)] hover:bg-[#F4F2FC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-orange)] disabled:opacity-50"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Rappel de l'activité d'origine, en lecture seule */}
          <section aria-label="Activité d'origine" className="rounded-xl p-3 space-y-2.5 bg-[var(--surface-2)] border border-[var(--bd)]">
            <p className="text-xs text-[var(--tx3)]">
              <span className="font-semibold text-[var(--brand-amber)]">{TYPE_LABELS[activity.type ?? ''] ?? activity.type ?? 'Activité'}</span>
              {' · '}
              <time dateTime={activity.performedAt}>{fmtDateTime(activity.performedAt)}</time>
            </p>
            <p className="text-sm font-semibold text-[var(--brand-dark)] break-words">{activity.subject || '—'}</p>
            {activity.description && (
              <div>
                <p className={sectionLabel}>Description</p>
                <p className="text-[13px] leading-relaxed whitespace-pre-wrap break-words" style={{ color: '#2D2546' }}>{activity.description}</p>
              </div>
            )}
            {activity.nextAction && (
              <div>
                <p className={sectionLabel}>Prochaine action</p>
                <p className="text-[13px] break-words" style={{ color: '#2D2546' }}>
                  {activity.nextAction}
                  {activity.nextActionDate && <span className="text-[var(--tx3)]"> — prévue le {fmtDay(activity.nextActionDate)}</span>}
                </p>
              </div>
            )}
            <ActivityCorrectionList activity={activity} />
          </section>

          <p id={helpId} className="text-xs leading-relaxed rounded-lg px-3 py-2.5 border border-[rgba(245,158,11,0.30)]" style={{ background: 'var(--amber-bg)', color: 'var(--amber-text)' }}>
            L'activité d'origine n'est pas modifiée. Votre rectification est ajoutée en dessous, datée et signée. Elle ne pourra pas être supprimée.
          </p>

          <div>
            <div className="flex items-baseline justify-between gap-3 mb-1.5">
              <label htmlFor={fieldId} className="text-xs font-medium text-[var(--tx2)]">Rectification *</label>
              <span id={countId} className="text-[11px] tabular-nums text-[var(--tx2)]">
                {content.length} / {MAX_LENGTH}
              </span>
            </div>
            <textarea
              ref={textareaRef}
              id={fieldId}
              required
              rows={5}
              maxLength={MAX_LENGTH}
              readOnly={mutation.isPending}
              value={content}
              onChange={e => { setContent(e.target.value); if (mutation.isError) mutation.reset() }}
              aria-describedby={`${helpId} ${countId}${mutation.isError ? ` ${errorId}` : ''}`}
              aria-invalid={isValidationError || undefined}
              placeholder="Ex. : le rendez-vous a eu lieu le 14/10 et non le 15/10."
              className="w-full px-3 py-2 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg text-sm text-[var(--brand-dark)] resize-y min-h-[112px] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] placeholder:text-[var(--tx3)]"
            />
          </div>

          {mutation.isError && (
            <p id={errorId} role="alert" className="text-xs rounded-lg px-3 py-2.5 bg-[var(--red-bg)] text-[var(--red-text)]">
              {errMessage(mutation.error, "La rectification n'a pas pu être ajoutée. Réessayez.")}
            </p>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 px-5 py-4 border-t border-[var(--bd)] shrink-0">
          <button
            type="button"
            onClick={close}
            disabled={mutation.isPending}
            className="min-h-[44px] sm:min-h-[36px] px-4 rounded-lg text-sm text-[var(--tx3)] hover:bg-[#F4F2FC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-orange)] disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="inline-flex items-center justify-center gap-2 min-h-[44px] sm:min-h-[36px] px-5 rounded-lg text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--brand-orange)] disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'var(--brand-orange)', color: '#FFFFFF' }}
          >
            {mutation.isPending
              ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
              : <PencilLine className="w-4 h-4" aria-hidden="true" />}
            {mutation.isPending ? 'Ajout en cours…' : 'Ajouter la rectification'}
          </button>
        </div>
      </form>
    </dialog>,
    document.body,
  )
}
